import { useState, useEffect } from 'react';
import {
  Card, CardContent, TextField, Button, Typography, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, useMediaQuery, useTheme, Container, Grid, Chip, Divider,
  ToggleButton, ToggleButtonGroup, alpha
} from '@mui/material';
import { CalendarToday, TrendingUp, TrendingDown, AccountBalance, Receipt, ViewModule } from '@mui/icons-material';
import * as colors from '../config/colorProfile';

const CashBook = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [view, setView] = useState('ledger');
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  const [cashBookEntries, setCashBookEntries] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(5000);
  
  // UPDATED DENOMINATIONS - Removed 2000, added proper structure
  const [denominations, setDenominations] = useState({
    500: 4,
    200: 5,
    100: 8,
    50: 10,
    20: 5,  // Added to coins
    10: 5,  // Added to coins
    5: 0,
    2: 0,
    1: 0,
    0.5: 0
  });

  useEffect(() => {
    fetchCashBookEntries();
  }, []);

  const fetchCashBookEntries = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `/api/cashbook?from=${dateRange.from}&to=${dateRange.to}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = await response.json();
      setCashBookEntries(data.entries || []);
      setOpeningBalance(data.openingBalance || 5000);
      if (data.denominations) {
        setDenominations(data.denominations);
      }
    } catch (error) {
      console.error('Failed to fetch cash book:', error);
      setCashBookEntries([
        {
          id: 1,
          date: '2025-11-18',
          particulars: 'Collection from Rajesh Kumar',
          receiptNo: 'RCP001',
          receiptAmount: 2500,
          paymentAmount: 0,
          type: 'receipt'
        },
        {
          id: 2,
          date: '2025-11-18',
          particulars: 'Collection from Priya Sharma',
          receiptNo: 'RCP002',
          receiptAmount: 1800,
          paymentAmount: 0,
          type: 'receipt'
        },
        {
          id: 3,
          date: '2025-11-18',
          particulars: 'Deposit to Branch',
          receiptNo: 'BR001',
          receiptAmount: 0,
          paymentAmount: 3000,
          type: 'payment'
        },
        {
          id: 4,
          date: '2025-11-17',
          particulars: 'Collection from Amit Patel',
          receiptNo: 'RCP003',
          receiptAmount: 3200,
          paymentAmount: 0,
          type: 'receipt'
        },
        {
          id: 5,
          date: '2025-11-17',
          particulars: 'Deposit to Bank',
          receiptNo: 'SL12345',
          receiptAmount: 0,
          paymentAmount: 2500,
          type: 'payment'
        },
        {
          id: 6,
          date: '2025-11-16',
          particulars: 'Collection from Sunita Reddy',
          receiptNo: 'RCP004',
          receiptAmount: 950,
          paymentAmount: 0,
          type: 'receipt'
        },
        {
          id: 7,
          date: '2025-11-16',
          particulars: 'Collection from Vikram Singh',
          receiptNo: 'RCP005',
          receiptAmount: 4200,
          paymentAmount: 0,
          type: 'receipt'
        }
      ]);
      setOpeningBalance(5000);
    }
  };

  const calculateRunningBalance = () => {
    let balance = openingBalance;
    return cashBookEntries.map(entry => {
      balance = balance + entry.receiptAmount - entry.paymentAmount;
      return { ...entry, balance };
    });
  };

  const calculateDenomTotal = (denom) => {
    return parseFloat(denom) * (denominations[denom] || 0);
  };

  const calculateTotalCashFromDenom = () => {
    return Object.keys(denominations).reduce((total, denom) => {
      return total + calculateDenomTotal(denom);
    }, 0);
  };

  const entriesWithBalance = calculateRunningBalance();
  const totalReceipts = cashBookEntries.reduce((sum, entry) => sum + entry.receiptAmount, 0);
  const totalPayments = cashBookEntries.reduce((sum, entry) => sum + entry.paymentAmount, 0);
  const closingBalance = openingBalance + totalReceipts - totalPayments;
  const denomTotal = calculateTotalCashFromDenom();

  return (
    <Box sx={{ 
      bgcolor: colors.PAGE_BG, 
      minHeight: '100vh', 
      py: isMobile ? 1 : 3,
      px: isMobile ? 0 : 2
    }}>
      <Container 
        maxWidth="xl"
        disableGutters={isMobile}
        sx={{ px: isMobile ? 0 : 2 }}
      >
        {/* Header */}
        <Box sx={{ 
          mb: isMobile ? 2 : 3, 
          px: isMobile ? 2 : 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography 
              variant={isMobile ? "h6" : "h4"} 
              sx={{ fontWeight: 700, color: colors.TEXT_PRIMARY, mb: 0.5 }}
            >
              Cash Book
            </Typography>
            <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>
              Track all cash receipts and payments
            </Typography>
          </Box>

          {/* View Toggle */}
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, newView) => newView && setView(newView)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                borderColor: colors.BORDER_COLOR,
                color: colors.TEXT_SECONDARY,
                '&.Mui-selected': {
                  bgcolor: colors.PRIMARY_COLOR,
                  color: 'white',
                  '&:hover': {
                    bgcolor: colors.PRIMARY_COLOR
                  }
                },
                '&:hover': {
                  bgcolor: colors.TOPBAR_HOVER
                }
              }
            }}
          >
            <ToggleButton value="ledger">
              <Receipt sx={{ fontSize: 18, mr: 0.5 }} />
              {!isMobile && 'Ledger'}
            </ToggleButton>
            <ToggleButton value="denomination">
              <ViewModule sx={{ fontSize: 18, mr: 0.5 }} />
              {!isMobile && 'Denominations'}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Date Filter - Only show in ledger view */}
        {view === 'ledger' && (
          <Card 
            elevation={0}
            sx={{ 
              border: isMobile ? 'none' : `1px solid ${colors.BORDER_COLOR}`,
              borderRadius: isMobile ? 0 : 2,
              mb: isMobile ? 0 : 2,
              borderBottom: `1px solid ${colors.BORDER_COLOR}`,
              bgcolor: colors.CARD_BG
            }}
          >
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <TextField
                  label="From Date"
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{
                    flex: isMobile ? '1 1 45%' : '0 0 180px',
                    '& .MuiOutlinedInput-root': { borderRadius: 1.5 }
                  }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{
                    flex: isMobile ? '1 1 45%' : '0 0 180px',
                    '& .MuiOutlinedInput-root': { borderRadius: 1.5 }
                  }}
                />
                <Button 
                  variant="contained" 
                  startIcon={<CalendarToday />}
                  onClick={fetchCashBookEntries}
                  size="small"
                  sx={{
                    flex: isMobile ? '1 1 100%' : '0 0 auto',
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    bgcolor: colors.PRIMARY_COLOR,
                    '&:hover': {
                      bgcolor: colors.PRIMARY_COLOR
                    }
                  }}
                >
                  Apply
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards - keeping same */}
        <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: isMobile ? 2 : 3, px: isMobile ? 2 : 0 }}>
          <Grid item xs={6} sm={3}>
            <Card 
              elevation={0}
              sx={{ 
                border: `1px solid ${colors.BORDER_COLOR}`,
                borderRadius: 2,
                bgcolor: colors.ACTION_YELLOW_BG
              }}
            >
              <CardContent sx={{ p: isMobile ? 2 : 3, textAlign: 'center' }}>
                <AccountBalance sx={{ color: colors.ACTION_YELLOW_TEXT, fontSize: isMobile ? 28 : 32, mb: 1 }} />
                <Typography variant="caption" sx={{ color: colors.ACTION_YELLOW_TEXT, fontWeight: 600, display: 'block' }}>
                  Opening Balance
                </Typography>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ color: colors.ACTION_YELLOW_TEXT, fontWeight: 700, mt: 0.5 }}>
                  ₹{openingBalance.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card 
              elevation={0}
              sx={{ 
                border: `1px solid ${colors.BORDER_COLOR}`,
                borderRadius: 2,
                bgcolor: colors.ACTION_GREEN_BG
              }}
            >
              <CardContent sx={{ p: isMobile ? 2 : 3, textAlign: 'center' }}>
                <TrendingUp sx={{ color: colors.ACTION_GREEN_TEXT, fontSize: isMobile ? 28 : 32, mb: 1 }} />
                <Typography variant="caption" sx={{ color: colors.ACTION_GREEN_TEXT, fontWeight: 600, display: 'block' }}>
                  Total Receipts
                </Typography>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ color: colors.ACTION_GREEN_TEXT, fontWeight: 700, mt: 0.5 }}>
                  ₹{totalReceipts.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card 
              elevation={0}
              sx={{ 
                border: `1px solid ${colors.BORDER_COLOR}`,
                borderRadius: 2,
                bgcolor: colors.STATUS_ERROR_BG
              }}
            >
              <CardContent sx={{ p: isMobile ? 2 : 3, textAlign: 'center' }}>
                <TrendingDown sx={{ color: colors.STATUS_ERROR_TEXT, fontSize: isMobile ? 28 : 32, mb: 1 }} />
                <Typography variant="caption" sx={{ color: colors.STATUS_ERROR_TEXT, fontWeight: 600, display: 'block' }}>
                  Total Payments
                </Typography>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ color: colors.STATUS_ERROR_TEXT, fontWeight: 700, mt: 0.5 }}>
                  ₹{totalPayments.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card 
              elevation={0}
              sx={{ 
                border: `1px solid ${colors.BORDER_COLOR}`,
                borderRadius: 2,
                bgcolor: colors.ACTION_BLUE_BG
              }}
            >
              <CardContent sx={{ p: isMobile ? 2 : 3, textAlign: 'center' }}>
                <AccountBalance sx={{ color: colors.ACTION_BLUE_TEXT, fontSize: isMobile ? 28 : 32, mb: 1 }} />
                <Typography variant="caption" sx={{ color: colors.ACTION_BLUE_TEXT, fontWeight: 600, display: 'block' }}>
                  Closing Balance
                </Typography>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ color: colors.ACTION_BLUE_TEXT, fontWeight: 700, mt: 0.5 }}>
                  ₹{closingBalance.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Ledger View - keeping same */}
        {view === 'ledger' && (
          <Card 
            elevation={0}
            sx={{ 
              border: isMobile ? 'none' : `1px solid ${colors.BORDER_COLOR}`,
              borderRadius: isMobile ? 0 : 2,
              bgcolor: colors.CARD_BG
            }}
          >
            <CardContent sx={{ p: isMobile ? 0 : 3 }}>
              <Typography 
                variant={isMobile ? "subtitle2" : "h6"} 
                sx={{ 
                  fontWeight: 600, 
                  color: colors.TEXT_PRIMARY, 
                  mb: 2,
                  px: isMobile ? 2 : 0,
                  pt: isMobile ? 2 : 0
                }}
              >
                Transaction Ledger
              </Typography>
              
              <Box sx={{ 
                maxHeight: isMobile ? 'calc(100vh - 480px)' : '500px',
                overflowY: 'auto',
                overflowX: 'auto',
                position: 'relative',
                border: `1px solid ${colors.BORDER_COLOR}`,
                borderRadius: isMobile ? 0 : 1.5
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        py: 1.5,
                        bgcolor: alpha(colors.CARD_BG, 0.7),
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        borderBottom: `2px solid ${colors.BORDER_COLOR}`,
                        color: colors.TEXT_PRIMARY
                      }}>
                        Date
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        py: 1.5,
                        bgcolor: alpha(colors.CARD_BG, 0.7),
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        borderBottom: `2px solid ${colors.BORDER_COLOR}`,
                        color: colors.TEXT_PRIMARY
                      }}>
                        Particulars
                      </TableCell>
                      {!isMobile && (
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          fontSize: '0.75rem', 
                          py: 1.5,
                          bgcolor: alpha(colors.CARD_BG, 0.7),
                          backdropFilter: 'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)',
                          position: 'sticky',
                          top: 0,
                          zIndex: 100,
                          borderBottom: `2px solid ${colors.BORDER_COLOR}`,
                          color: colors.TEXT_PRIMARY
                        }}>
                          Receipt No
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        py: 1.5,
                        bgcolor: alpha(colors.CARD_BG, 0.7),
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        borderBottom: `2px solid ${colors.BORDER_COLOR}`,
                        color: colors.TEXT_PRIMARY
                      }}>
                        {isMobile ? 'In' : 'Receipt'}
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        py: 1.5,
                        bgcolor: alpha(colors.CARD_BG, 0.7),
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        borderBottom: `2px solid ${colors.BORDER_COLOR}`,
                        color: colors.TEXT_PRIMARY
                      }}>
                        {isMobile ? 'Out' : 'Payment'}
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        py: 1.5,
                        bgcolor: alpha(colors.CARD_BG, 0.7),
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        borderBottom: `2px solid ${colors.BORDER_COLOR}`,
                        color: colors.TEXT_PRIMARY
                      }}>
                        Balance
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Opening Balance Row */}
                    <TableRow sx={{ bgcolor: colors.ACTION_YELLOW_BG }}>
                      <TableCell sx={{ fontSize: '0.75rem', py: 1.5, fontWeight: 600 }}>
                        {new Date(dateRange.from).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </TableCell>
                      <TableCell colSpan={isMobile ? 1 : 2} sx={{ fontSize: '0.75rem', py: 1.5, fontWeight: 600 }}>
                        Opening Balance
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1.5 }}>-</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1.5 }}>-</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1.5, fontWeight: 700, color: colors.ACTION_YELLOW_TEXT }}>
                        ₹{openingBalance.toLocaleString()}
                      </TableCell>
                    </TableRow>

                    {/* Transaction Rows */}
                    {entriesWithBalance.map((entry) => (
                      <TableRow key={entry.id} hover sx={{ '&:hover': { bgcolor: colors.CARD_HOVER } }}>
                        <TableCell sx={{ fontSize: '0.75rem', py: 1.5 }}>
                          {new Date(entry.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.TEXT_PRIMARY }}>
                            {entry.particulars}
                          </Typography>
                          {isMobile && (
                            <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>
                              {entry.receiptNo}
                            </Typography>
                          )}
                        </TableCell>
                        {!isMobile && (
                          <TableCell sx={{ fontSize: '0.75rem', py: 1.5 }}>
                            <Chip 
                              label={entry.receiptNo}
                              size="small"
                              sx={{ 
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                bgcolor: alpha(colors.PRIMARY_COLOR, 0.1),
                                color: colors.PRIMARY_COLOR
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1.5 }}>
                          {entry.receiptAmount > 0 ? (
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.SUCCESS_COLOR }}>
                              +₹{entry.receiptAmount.toLocaleString()}
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1.5 }}>
                          {entry.paymentAmount > 0 ? (
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.ERROR_COLOR }}>
                              -₹{entry.paymentAmount.toLocaleString()}
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1.5, fontWeight: 700, color: colors.TEXT_PRIMARY }}>
                          ₹{entry.balance.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Closing Balance Row */}
                    <TableRow sx={{ bgcolor: colors.ACTION_BLUE_BG }}>
                      <TableCell sx={{ fontSize: '0.75rem', py: 1.5, fontWeight: 700 }}>
                        {new Date(dateRange.to).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </TableCell>
                      <TableCell colSpan={isMobile ? 1 : 2} sx={{ fontSize: '0.75rem', py: 1.5, fontWeight: 700 }}>
                        Closing Balance
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1.5, fontWeight: 700, color: colors.SUCCESS_COLOR }}>
                        ₹{totalReceipts.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1.5, fontWeight: 700, color: colors.ERROR_COLOR }}>
                        ₹{totalPayments.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.875rem', py: 1.5, fontWeight: 700, color: colors.PRIMARY_COLOR }}>
                        ₹{closingBalance.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Formula Info */}
              <Box sx={{ 
                mt: 3, 
                p: 2, 
                bgcolor: alpha(colors.PRIMARY_COLOR, 0.05),
                borderRadius: 1.5,
                mx: isMobile ? 2 : 0,
                mb: isMobile ? 2 : 0,
                border: `1px solid ${colors.BORDER_COLOR}`
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: colors.TEXT_SECONDARY }}>
                  Cash Book Formula:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: colors.TEXT_PRIMARY }}>
                  Closing Balance = Opening Balance + Total Receipts - Total Payments
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: colors.PRIMARY_COLOR, mt: 0.5 }}>
                  ₹{closingBalance.toLocaleString()} = ₹{openingBalance.toLocaleString()} + ₹{totalReceipts.toLocaleString()} - ₹{totalPayments.toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Denomination View - UPDATED */}
        {view === 'denomination' && (
          <Card 
            elevation={0}
            sx={{ 
              border: isMobile ? 'none' : `1px solid ${colors.BORDER_COLOR}`,
              borderRadius: isMobile ? 0 : 2,
              bgcolor: colors.CARD_BG
            }}
          >
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography 
                variant={isMobile ? "subtitle2" : "h6"} 
                sx={{ fontWeight: 600, color: colors.TEXT_PRIMARY, mb: 3 }}
              >
                Cash Balance by Denomination
              </Typography>

              <TableContainer 
                component={Paper} 
                elevation={0}
                sx={{ 
                  border: `1px solid ${colors.BORDER_COLOR}`,
                  borderRadius: 1.5,
                  maxHeight: isMobile ? 'calc(100vh - 380px)' : 'none'
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(colors.PRIMARY_COLOR, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1.5 }}>
                        Denomination
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1.5 }}>
                        Count
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1.5 }}>
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ bgcolor: alpha(colors.INFO_COLOR, 0.1), fontWeight: 600, fontSize: '0.875rem', py: 1 }}>
                        Currency Notes
                      </TableCell>
                    </TableRow>
                    {/* REMOVED 2000, keeping only 500, 200, 100, 50 */}
                    {[500, 200, 100, 50].map((denom) => (
                      <TableRow key={denom} hover sx={{ '&:hover': { bgcolor: colors.CARD_HOVER } }}>
                        <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, py: 1.5 }}>
                          ₹{denom}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          <Chip 
                            label={denominations[denom] || 0}
                            size="small"
                            sx={{
                              bgcolor: colors.ACTION_BLUE_BG,
                              color: colors.ACTION_BLUE_TEXT,
                              fontWeight: 700,
                              minWidth: 50
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 2,
                              py: 0.5,
                              bgcolor: alpha(colors.PRIMARY_COLOR, 0.1),
                              borderRadius: 1,
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              color: colors.PRIMARY_COLOR,
                              minWidth: 80
                            }}
                          >
                            ₹{calculateDenomTotal(denom).toLocaleString()}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}

                    <TableRow>
                      <TableCell colSpan={3} sx={{ bgcolor: alpha(colors.INFO_COLOR, 0.1), fontWeight: 600, fontSize: '0.875rem', py: 1 }}>
                        Coins in Circulation
                      </TableCell>
                    </TableRow>
                    {/* ADDED 20 and 10 to coins section */}
                    {[20, 10, 5, 2, 1, 0.5].map((denom) => (
                      <TableRow key={denom} hover sx={{ '&:hover': { bgcolor: colors.CARD_HOVER } }}>
                        <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, py: 1.5 }}>
                          ₹{denom === 0.5 ? '0.50 (50 paise)' : denom}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          <Chip 
                            label={denominations[denom] || 0}
                            size="small"
                            sx={{
                              bgcolor: colors.ACTION_BLUE_BG,
                              color: colors.ACTION_BLUE_TEXT,
                              fontWeight: 700,
                              minWidth: 50
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 2,
                              py: 0.5,
                              bgcolor: alpha(colors.PRIMARY_COLOR, 0.1),
                              borderRadius: 1,
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              color: colors.PRIMARY_COLOR,
                              minWidth: 80
                            }}
                          >
                            ₹{calculateDenomTotal(denom).toLocaleString()}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}

                    <TableRow sx={{ bgcolor: colors.PRIMARY_COLOR }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 700, color: 'white', fontSize: '1rem', py: 2 }}>
                        Total Cash in Hand
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'white', fontSize: '1.125rem', py: 2 }}>
                        ₹{denomTotal.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ 
                mt: 3, 
                p: 2, 
                bgcolor: denomTotal === closingBalance ? colors.STATUS_SUCCESS_BG : colors.STATUS_WARNING_BG,
                borderRadius: 1.5,
                border: `1px solid ${denomTotal === closingBalance ? colors.SUCCESS_COLOR : colors.WARNING_COLOR}`
              }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>
                      From Denominations
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: colors.TEXT_PRIMARY }}>
                      ₹{denomTotal.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>
                      Closing Balance
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: colors.TEXT_PRIMARY }}>
                      ₹{closingBalance.toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
                {denomTotal !== closingBalance && (
                  <Typography variant="body2" sx={{ color: colors.WARNING_COLOR, fontWeight: 600, mt: 1 }}>
                    ⚠ Variance: ₹{Math.abs(denomTotal - closingBalance).toLocaleString()}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default CashBook;
